import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Icon,
  Panel,
  Stat,
  Tag,
  useErrorDialog,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { brandColumns } from './columns.tsx';
import { EditBrandDialog } from './EditBrandDialog.tsx';
import { NewBrandDialog } from './NewBrandDialog.tsx';
import { RenewLicenceDialog } from './RenewLicenceDialog.tsx';
import { LICENCE_LABEL, day, daysUntil, licenceState, type BrandRow } from './types.ts';

/**
 * BRANDS & LICENCES — §4.
 *
 * The screen that did not exist, for the writes that did not exist. Until this
 * module `platform.brand` had exactly one writer in the repository —
 * `packages/db/src/seed.ts:184` — so a brand could only be created by a
 * developer running the seed, and `licence_expires_at` could not be moved by
 * anybody. The dashboard's 60-day advance warning has always been correct and
 * has always been unactionable: it counted down to an expiry nobody could
 * renew.
 *
 * ── WHAT IS GATED, AND ON WHAT ─────────────────────────────────────────────
 *
 * The SCREEN is `platform.view` — the same capability as /settings and /system,
 * held by Super Admin, Finance Lead, Risk Officer and Auditor. Gating it on the
 * write would mean the only way to let somebody SEE a licence expiry is to hand
 * them the power to move it, which is the exact mistake `platform.view` was
 * added to undo.
 *
 * Every CONTROL is `platform.manage`, and when the operator does not hold it
 * the control is disabled with the true reason on it rather than hidden. This
 * is a deliberate exception to the console's "permission-gated actions are
 * hidden, not disabled" rule, and the reason is specific to this screen: an
 * Auditor looking at a licence 12 days from lapsing needs to know that renewal
 * is somebody's job and whose, not to see a screen with no controls on it at
 * all and conclude the platform cannot renew a licence.
 */
export function Brands() {
  const { allowed, brandId, brands: sessionBrands } = useSession();
  const { showError } = useErrorDialog();

  const [rows, setRows] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState(true);
  /*
   * Did the read FAIL, or did it succeed and return nothing?
   *
   * The error dialog is dismissed in a second, and what stands behind it is the
   * whole answer an operator takes away. Without this flag the table falls
   * through to the empty state written for a genuinely empty platform and every
   * tile reads 0 — a confident zero produced by a request that never came back.
   */
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BrandRow | null>(null);
  const [renewing, setRenewing] = useState<BrandRow | null>(null);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);

  const mayManage = allowed('platform.manage');

  const mayRead = allowed('platform.view');

  useEffect(() => {
    /* Not fired at all without the capability. The wall below renders instead,
       and a request we already know will 403 would put an error dialog in front
       of the wall that was about to explain the refusal properly. */
    if (!mayRead) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<{ items: BrandRow[] }>('/api/platform/brands')
      .then((data) => {
        if (cancelled) return;
        setRows(data.items);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setRows([]);
        setFailed(true);
        showError({
          title: 'Could not load brands and licences',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The brand list could not be read. Nothing below is a real zero.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload, showError, mayRead]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);


  /*
   * The header's brand selector narrows this screen, and it is the only filter
   * on it. `GET /api/platform/brands` is NOT brand-scoped server-side — it is a
   * `platform.view` read of the platform's own configuration — so an admin
   * restricted to one brand still receives every row, and the write routes are
   * what refuse them. Narrowing here is a convenience, not a boundary, and it
   * is never the thing standing between an operator and another brand's data.
   */
  const scopedName = useMemo(
    () => sessionBrands.find((b) => b.id === brandId)?.name ?? null,
    [sessionBrands, brandId],
  );
  const visible = useMemo(
    () => (brandId ? rows.filter((b) => b.id === brandId) : rows),
    [rows, brandId],
  );

  /*
   * The capability wall, after the hooks and before anything renders.
   *
   * The sidebar already hides this module from a role without `platform.view`,
   * but a link, a bookmark or a typed address does not go through the sidebar —
   * and a screen that mounted anyway would fire GET /api/platform/brands, take
   * a 403, and put an error dialog in front of the operator instead of telling
   * them which capability they are missing. The same wall /settings and /system
   * carry, for the same reason and with the same key.
   *
   * It sits BELOW every hook. An early return above a useMemo is a conditional
   * hook, and React's rules are not negotiable about that.
   */
  if (!mayRead) {
    return (
      <EmptyState icon="lock-key" title="Brands & licences">
        This module needs the platform.view capability. Your role does not hold it. Reading which
        brands exist and when their licences expire is a separate key from changing them — that one
        is platform.manage — and you hold neither.
      </EmptyState>
    );
  }

  const selected = visible.find((b) => b.ref === selectedRef) ?? null;

  const live = visible.filter((b) => b.active);
  const expiring = visible.filter((b) => licenceState(b) === 'EXPIRING');
  const lapsed = visible.filter((b) => licenceState(b) === 'EXPIRED');
  const unlicensed = visible.filter((b) => licenceState(b) === 'NONE');
  const soonest = visible
    .filter((b) => b.licence_expires_at !== null)
    .toSorted(
      (a, b) =>
        new Date(a.licence_expires_at!).getTime() - new Date(b.licence_expires_at!).getTime(),
    )[0];
  const totalPlayers = visible.reduce((sum, b) => sum + Number(b.players), 0);

  /** A tile value when the read failed. Never a zero nobody measured. */
  const unread = <span className="text-subtle">not read — the request failed</span>;

  /** The true reason a control is off, on the control itself. */
  const refusal = mayManage ? undefined : 'You do not hold platform.manage, which every write on this screen requires.';

  return (
    <>
      {/*
        WHY THE SENTENCE ABOUT THIS SCREEN SITS HERE, AND NOT IN A NOTICE.

        The reason this module exists is written at the top of this file, and
        until now it was read only by whoever was editing the file. An operator
        arriving here met a title, four figures and a table, and had to work out
        from the figures that this is where a licence date gets moved — which is
        the one thing the dashboard has been counting down to for sixty days
        without offering anybody a way to act on it.

        It could not become a notice lower down the page instead. The two
        notices below this header are about the DATA — which licences are
        lapsing, which brands have no expiry recorded at all — and a third
        prose block stacked above them would be read as one wall and skipped as
        one wall. The header is the one place a sentence about the SCREEN can
        sit without competing with a statement about the rows.
      */}
      <ScreenHeader
        title="Brands & licences"
        purpose="For whoever answers for the brands: set one up, and keep each licence date current so a lapse is caught before it happens."
        /*
          The caption kept exactly as it was, all three parts of it.

          The first part is tri-state and the three states are not
          interchangeable. 'Loading…' means the answer has not arrived yet;
          'brand list not read' means the request failed, so the counts that
          would have stood here were never made; and a count means the platform
          answered. Collapsing that middle state into a zero is the precise
          mistake the long note beside `failed` above exists to prevent — "this
          platform has no brands at all" is a fact somebody acts on, and "nobody
          asked the question successfully" is not — and every tile, the table's
          empty state and this line all lean on the same flag for the same
          reason.

          The one-host-one-brand clause and the brand-scope suffix stay SEPARATE
          expressions appended to whichever state won, because both are true of
          all three: a failed read still failed inside a scope, and a reader who
          does not know whether they are looking at one brand or at all of them
          cannot judge the number either way.
        */
        meta={
          <>
            {loading
              ? 'Loading…'
              : failed
                ? 'brand list not read'
                : `${formatCount(visible.length)} brands · ${formatCount(live.length)} live`}
            {' · every host resolves to exactly one brand'}
            {scopedName ? ` · scoped to ${scopedName}` : ''}
          </>
        }
        /*
          The refusal wiring moves across untouched, disabled state and all.

          This screen's deliberate exception to the console's "hide what the
          operator may not do" rule is explained at the top of the file: an
          Auditor looking at a licence twelve days from lapsing needs to see
          that renewal is somebody's job, not an empty toolbar they would read
          as the platform being unable to renew a licence at all. Moving the
          button into the header is a layout change and must not quietly become
          a behaviour change, so the disabled flag, the spoken label and the
          hover text are the same ones that were on it before.
        */
        actions={
          <Button
            variant="primary"
            disabled={!mayManage}
            aria-label={mayManage ? undefined : `New brand — ${refusal}`}
            title={refusal}
            onClick={() => setCreating(true)}
          >
            <Icon name="plus" />
            New brand
          </Button>
        }
      />

      {expiring.length > 0 || lapsed.length > 0 ? (
        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            {lapsed.length > 0 ? (
              <>
                {lapsed.length === 1
                  ? `${lapsed[0]!.name}'s licence has LAPSED`
                  : `${formatCount(lapsed.length)} brand licences have LAPSED`}
                . A lapsed licence does not stop the platform serving that brand — nothing here
                enforces one — so it is an operational and regulatory fact, not a technical block.{' '}
              </>
            ) : null}
            {expiring.length > 0 ? (
              <>
                {expiring.length === 1
                  ? `${expiring[0]!.name}'s licence expires ${day(expiring[0]!.licence_expires_at)}`
                  : `${formatCount(expiring.length)} brand licences expire`}{' '}
                inside the 60-day advance-warning window. Renewal is a regulator lead time, not an
                operational one — start it now, and record it here when it comes back.
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {unlicensed.length > 0 && !failed ? (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            {unlicensed.length === 1
              ? `${unlicensed[0]!.name} has no licence expiry recorded`
              : `${formatCount(unlicensed.length)} brands have no licence expiry recorded`}
            , so the dashboard cannot warn about {unlicensed.length === 1 ? 'it' : 'them'} at all.
            An absent date is not an unexpired one.
          </div>
        </div>
      ) : null}

      <div className="stat-grid">
        <Stat
          label="Brands"
          value={loading || failed ? '—' : formatCount(visible.length)}
          delta={
            failed
              ? 'not counted — the brand read failed'
              : loading
                ? undefined
                : `${formatCount(live.length)} live · ${formatCount(visible.length - live.length)} suspended`
          }
        />
        <Stat
          label="Players across these brands"
          value={loading || failed ? '—' : formatCount(totalPlayers)}
          delta={
            failed
              ? 'not counted — the brand read failed'
              : loading
                ? undefined
                : 'counted per brand, from the players themselves'
          }
        />
        <Stat
          label="Licences inside the 60-day window"
          value={loading || failed ? '—' : formatCount(expiring.length + lapsed.length)}
          delta={
            failed
              ? 'not counted — the brand read failed'
              : loading
                ? undefined
                : lapsed.length > 0
                  ? `${formatCount(lapsed.length)} already lapsed`
                  : expiring.length > 0
                    ? 'renewable from this screen'
                    : 'every licence is further out than 60 days'
          }
        />
        <Stat
          label="Earliest licence expiry"
          value={
            failed ? (
              unread
            ) : loading ? (
              '—'
            ) : soonest ? (
              day(soonest.licence_expires_at)
            ) : (
              <span className="text-subtle">no licence recorded on any brand</span>
            )
          }
          delta={
            failed || loading || !soonest
              ? undefined
              : `${soonest.name} · ${formatCount(daysUntil(soonest.licence_expires_at!))} days`
          }
        />
      </div>

      <Panel title="Brands">
        {loading && rows.length === 0 ? (
          <EmptyState icon="hourglass" title="Reading the brand list">
            One request, for every brand.
          </EmptyState>
        ) : failed ? (
          <EmptyState icon="warning-circle" title="The brand list could not be read">
            This is <strong>not</strong> an empty platform — the request failed, so nothing above is
            a measured figure. Reload the screen; if it keeps failing, tell whoever runs the
            platform.
          </EmptyState>
        ) : (
          <DataTable
            columns={brandColumns}
            rows={visible}
            rowKey={(b) => b.id}
            caption="Brands, their hosts and their licences"
            onRowClick={(b) => setSelectedRef(b.ref)}
            empty={
              <EmptyState icon="buildings" title="No brands">
                {brandId
                  ? 'The brand selector at the top of the screen is narrowing this list to one brand. Clear it to see the rest.'
                  : 'This platform has no brands at all, which means no player can register anywhere — the portal resolves a registration by matching its host against a brand. Create the first one.'}
              </EmptyState>
            }
          />
        )}
      </Panel>

      <Drawer
        open={selected !== null}
        onClose={() => setSelectedRef(null)}
        title={selected?.name ?? 'Brand'}
        subtitle={
          selected ? (
            <span className="tabular">
              {selected.ref} · {selected.domain}
            </span>
          ) : undefined
        }
        actions={
          selected ? (
            <>
              <Button
                disabled={!mayManage}
                aria-label={mayManage ? undefined : `Edit brand — ${refusal}`}
                title={refusal}
                onClick={() => setEditing(selected)}
              >
                Edit
              </Button>
              <Button
                variant="primary"
                disabled={!mayManage}
                aria-label={mayManage ? undefined : `Renew licence — ${refusal}`}
                title={refusal}
                onClick={() => setRenewing(selected)}
              >
                Renew licence
              </Button>
            </>
          ) : undefined
        }
      >
        {selected ? (
          <div className="stack">
            <dl className="stack" style={{ margin: 0 }}>
              <div className="def-row">
                <dt>Reference</dt>
                <dd className="tabular">{selected.ref}</dd>
              </div>
              <div className="def-row">
                <dt>Host</dt>
                <dd className="tabular">{selected.domain}</dd>
              </div>
              <div className="def-row">
                <dt>Status</dt>
                <dd>
                  <Tag tone={selected.active ? 'accent' : 'quiet'}>
                    {selected.active ? 'Live' : 'Suspended'}
                  </Tag>
                </dd>
              </div>
              <div className="def-row">
                <dt>Players</dt>
                <dd className="tabular">{formatCount(Number(selected.players))}</dd>
              </div>
              <div className="def-row">
                <dt>Licence</dt>
                <dd className="tabular">
                  {selected.licence_ref ?? <span className="text-subtle">none recorded</span>}
                </dd>
              </div>
              <div className="def-row">
                <dt>Licence expires</dt>
                <dd className="tabular">
                  {selected.licence_expires_at ? (
                    <>
                      {day(selected.licence_expires_at)} · {LICENCE_LABEL[licenceState(selected)]}
                    </>
                  ) : (
                    <span className="text-subtle">no expiry recorded</span>
                  )}
                </dd>
              </div>
            </dl>

            <div className="notice notice-quiet">
              <Icon name="globe" />
              <div>
                <span className="tabular">{selected.domain}</span> is a{' '}
                <strong>tenancy boundary</strong>. The player portal resolves which brand a new
                registration belongs to by matching the request&rsquo;s Host header against it —
                there is no field in any player request that could override that. One host resolves
                to exactly one brand, whatever its capitalisation, and the platform refuses a second
                brand on the same host.
              </div>
            </div>

            {!selected.active ? (
              <div className="notice">
                <Icon name="warning-circle" />
                <div>
                  This brand is off the floor. Its host serves nobody and its{' '}
                  {formatCount(Number(selected.players))} players cannot sign in — the player site
                  answers only for a brand that is on the floor. Nothing has been deleted; Edit puts
                  it back.
                </div>
              </div>
            ) : null}

            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                What this drawer does <strong>not</strong> show, because the platform does not
                gather it per brand in one place: revenue, the games enabled, the payment methods
                available and the agents attributed. Each lives on its own screen, narrowed by the
                brand selector at the top of the console.
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>

      <NewBrandDialog open={creating} onClose={() => setCreating(false)} onDone={refresh} />
      <EditBrandDialog brand={editing} onClose={() => setEditing(null)} onDone={refresh} />
      <RenewLicenceDialog brand={renewing} onClose={() => setRenewing(null)} onDone={refresh} />
    </>
  );
}
